// Social platforms shared by Competencia (and the upcoming Marketing module):
// the values of the `platform` check, their labels, icons and filters.
// The icons live in PlatformIcon.tsx.
import type { SocialPlatform } from "./types";

/** Every value accepted by the database, in display order. */
export const PLATFORMS: readonly SocialPlatform[] = [
  "instagram",
  "linkedin",
  "x",
  "youtube",
  "tiktok",
  "facebook",
  "web",
  "otro",
];

export const isPlatform = (value: unknown): value is SocialPlatform =>
  PLATFORMS.includes(value as SocialPlatform);

export const platformLabelKey = (platform: SocialPlatform): string =>
  `competition.platforms.${isPlatform(platform) ? platform : "otro"}`;

/** Filter chips: the big networks one by one, the rest grouped. */
export type PlatformFilter =
  "all" | Exclude<SocialPlatform, "facebook" | "web" | "otro"> | "others";

export const PLATFORM_FILTERS: readonly PlatformFilter[] = [
  "all",
  "x",
  "instagram",
  "youtube",
  "tiktok",
  "linkedin",
  "others",
];

export const isPlatformFilter = (value: unknown): value is PlatformFilter =>
  PLATFORM_FILTERS.includes(value as PlatformFilter);

export const platformFilterLabelKey = (filter: PlatformFilter): string =>
  filter === "all" || filter === "others"
    ? `competition.platform_filter.${filter}`
    : platformLabelKey(filter);

const GROUPED: readonly SocialPlatform[] = ["facebook", "web", "otro"];

export const matchesPlatformFilter = (
  platform: SocialPlatform,
  filter: PlatformFilter,
): boolean =>
  filter === "all" ||
  (filter === "others"
    ? GROUPED.includes(platform) || !isPlatform(platform)
    : platform === filter);

const PROFILE_URL: Partial<Record<SocialPlatform, (user: string) => string>> = {
  instagram: (user) => `https://www.instagram.com/${user}/`,
  x: (user) => `https://x.com/${user}`,
  tiktok: (user) => `https://www.tiktok.com/@${user}`,
  youtube: (user) => `https://www.youtube.com/@${user}`,
  facebook: (user) => `https://www.facebook.com/${user}`,
};

/**
 * Best-guess public URL of a handle ("@evoost_ai" on Instagram ->
 * https://www.instagram.com/evoost_ai/; "aizora.es" on the web ->
 * https://aizora.es). Null when the network has no predictable URL.
 */
export const profileUrlOf = (
  platform: SocialPlatform,
  handle: string,
): string | null => {
  const user = handle.trim().replace(/^@/, "");
  if (!user || /\s/.test(user)) return null;
  if (/^https?:\/\//i.test(user)) return user;
  if (platform === "web") return `https://${user}`;
  return PROFILE_URL[platform]?.(user) ?? null;
};
